import React, { useEffect, useRef, useState } from 'react';
import { useContactForm } from '../context/ContactFormContext';
import gsap from 'gsap';
// Videos are Git LFS; run `git lfs pull` if hero videos do not display.
import v1 from './assets/videos/CadburyDairyMilkwithoutwatermark.mp4';
import v2 from './assets/videos/ChatGPT.mp4';
import v3 from './assets/videos/LintLindor.mp4';
import v4 from './assets/videos/Pandora.mp4';
import v5 from './assets/videos/Primark.mp4';
import v6 from './assets/videos/Rang Rasiya Red maroon.mp4';
import v7 from './assets/videos/RangRasiyaBlue.mp4';
import v8 from './assets/videos/StellaArtios.mp4';
import v9 from './assets/videos/Tiffany&co.mp4';
import v10 from './assets/videos/YankeeCandleHotHotChocolate.mp4';
import v11 from './assets/videos/yankeecandleLavenderwithoutwatermark.mp4';

// Primark first (index 1); rest same order as Video Ads slider: Rang Rasiya Red, Tiffany, Pandora, Yankee Lavender, Rang Rasiya Blue, Cadbury, ChatGPT, Lindt, Stella, Yankee Hot Chocolate
const HERO_VIDEOS = [v5, v6, v9, v4, v11, v7, v1, v2, v3, v8, v10];
const HERO_COUNT = HERO_VIDEOS.length;

const HEADLINE_LINES = ['AI Video Ad', 'Creative', 'That', 'Drives', 'Revenue'];

const Hero = () => {
  const { openForm } = useContactForm();
  const [currentSlide, setCurrentSlide] = useState(0);
  const contentRef = useRef(null);
  const headlineRef = useRef(null);
  const rightColRef = useRef(null);
  const hasAnimatedRef = useRef(false);

  // Left headline: one letter at a time; then right column pop-in
  useEffect(() => {
    const t = setTimeout(() => {
      const headline = headlineRef.current;
      const rightCol = rightColRef.current;
      if (!headline || hasAnimatedRef.current) return;

      const letters = headline.querySelectorAll('.hero-letter');
      if (letters.length === 0) return;

      hasAnimatedRef.current = true;
      gsap.set(letters, { opacity: 0, y: 14 });
      if (rightCol) gsap.set(rightCol, { opacity: 0, scale: 0.75, y: 24 });

      const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });
      tl.to(letters, {
        opacity: 1,
        y: 0,
        duration: 0.4,
        stagger: 0.06,
        ease: 'power2.out',
        overwrite: 'auto',
      });
      if (rightCol) {
        tl.to(
          rightCol,
          { opacity: 1, scale: 1, y: 0, duration: 0.7, ease: 'back.out' },
          '-=0.4'
        );
      }
    }, 350);
    return () => clearTimeout(t);
  }, []);

  // Subtle re-appear when changing slide (optional)
  const goToSlide = (index) => {
    if (index === currentSlide) return;
    setCurrentSlide(index);
  };

  const goPrev = () => setCurrentSlide((s) => (s - 1 + HERO_COUNT) % HERO_COUNT);
  const goNext = () => setCurrentSlide((s) => (s + 1) % HERO_COUNT);

  return (
    <section
      className="relative bg-surface min-h-screen min-h-[100dvh] items-center pt-8 sm:pt-10 md:pt-16 overflow-hidden w-full"
      id="hero"
    >
      {/* Background Video - current slide (key forces remount so autoPlay runs) */}
      <video
        key={currentSlide}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover z-0"
        style={{ minHeight: '100%', minWidth: '100%' }}
        src={HERO_VIDEOS[currentSlide]}
      >
        <source src={HERO_VIDEOS[currentSlide]} type="video/mp4" />
      </video>

      {/* Overlay: darker so text is prominent and readable over video */}
      <div className="absolute inset-0 bg-black/50 z-0" />

      <div className="min-h-screen flex flex-col items-center justify-center relative z-10 py-6 sm:py-10 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex flex-col justify-center">
          <div
            ref={contentRef}
            className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-6 lg:gap-8 xl:gap-10 items-center justify-items-center lg:justify-items-stretch w-full max-w-5xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl mx-auto"
          >
            {/* Left Section - Main Headline (one letter at a time); on mobile appears on top (order-1) */}
            <div ref={headlineRef} className="order-1 lg:order-1 w-full flex justify-center lg:justify-end lg:pr-4">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-extrabold text-white leading-[1.1] tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]">
                {HEADLINE_LINES.map((line, lineIndex) => (
                  <div key={lineIndex} className="block whitespace-nowrap overflow-hidden">
                    {line.split('').map((char, i) => (
                      <span
                        key={`${lineIndex}-${i}`}
                        className="hero-letter inline-block"
                        style={char === ' ' ? { width: '0.2em', minWidth: '0.2em' } : undefined}
                      >
                        {char}
                      </span>
                    ))}
                  </div>
                ))}
              </h1>
            </div>

            {/* Right Section - Description and CTA (animated); on mobile appears below headline (order-2) */}
            <div ref={rightColRef} className="order-2 lg:order-2 space-y-4 sm:space-y-6 w-full max-w-xl mx-auto lg:mx-0 lg:pl-4 text-center lg:text-left">
              <div className="space-y-1 sm:space-y-2">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#ffd166] leading-tight drop-shadow-[0_1px_4px_rgba(0,0,0,0.3)]">
                  AI video ads.
                </h1>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#ffd166] leading-tight drop-shadow-[0_1px_4px_rgba(0,0,0,0.3)]">
                  Made in minutes. Built to perform.
                </h1>
              </div>

              <p className="text-white/95 text-sm sm:text-base md:text-lg leading-relaxed font-normal drop-shadow-[0_1px_3px_rgba(0,0,0,0.4)] max-w-xl">
                You need to drive revenue. You need a creative edge. You need to do it fast.
                Capture your advertiser's attention and get them on air in record time with
                custom AI video ads. Autometa Studio helps you launch campaigns faster,
                scale effortlessly and drive real revenue.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button
                  onClick={openForm}
                  className="bg-brand hover:bg-[#C96A4A] text-white px-6 py-2.5 sm:px-8 sm:py-3 rounded-lg font-semibold transition-colors text-sm sm:text-base md:text-lg shadow-lg shadow-black/20"
                >
                  Contact Sales
                </button>
              </div>
            </div>
          </div>

          {/* Video selector: just below text content */}
          <div className="flex items-center justify-center gap-4 mt-10 sm:mt-12">
        <button
          type="button"
          onClick={goPrev}
          className="p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Previous video"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="flex items-center gap-2">
          {Array.from({ length: HERO_COUNT }, (_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goToSlide(i)}
              className={`rounded-full transition-all ${
                i === currentSlide
                  ? 'w-8 h-2.5 bg-white'
                  : 'w-2.5 h-2.5 bg-white/50 hover:bg-white/80'
              }`}
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === currentSlide ? 'true' : undefined}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={goNext}
          className="p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Next video"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
